import { useMemo } from 'react'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../types/plans'
import { SortableStageCard } from './SortableStageCard'
import { getServices } from './constructorTypes'
import type { ConstructorPlan, ConstructorStage, DoctorOption } from './constructorTypes'

export function ConstructorStagesEditor({
  planDetail,
  stages,
  stageOrder,
  totalAmount,
  bootstrap,
  session,
  doctors,
  onReload,
  onAddStage,
}: {
  planDetail: ConstructorPlan
  stages: ConstructorStage[]
  stageOrder: number[]
  totalAmount: number
  bootstrap: TenantBootstrapModel
  session: AuthSession
  doctors: DoctorOption[]
  onReload: () => void
  onAddStage: () => void
}) {
  const orderedStages = useMemo(() => {
    if (stageOrder.length === 0) {
      return stages
    }
    const byId = new Map(stages.map((stage) => [stage.id, stage]))
    return stageOrder
      .map((id) => byId.get(id))
      .filter((stage): stage is ConstructorStage => stage != null)
  }, [stages, stageOrder])

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
      <SortableContext items={stageOrder} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {orderedStages.map((stage) => (
            <SortableStageCard
              key={stage.id}
              stage={stage}
              planId={planDetail.id}
              bootstrap={bootstrap}
              session={session}
              capabilities={planDetail.capabilities}
              doctors={doctors}
              onReload={onReload}
              dropTargetId={`stage-drop:${stage.id}`}
            />
          ))}
        </div>
      </SortableContext>

      <button
        type="button"
        className="mt-3 w-full rounded-md border border-dashed border-slate-300 bg-white py-2 text-sm font-medium text-slate-500 hover:border-blue-400 hover:text-blue-600"
        onClick={onAddStage}
      >
        + Добавить этап
      </button>

      {stages.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-4 rounded-md border border-slate-200 bg-white px-4 py-2 text-xs text-slate-600">
          <span>
            Этапов: <span className="font-semibold text-slate-800">{stages.length}</span>
          </span>
          <span>
            Услуг: <span className="font-semibold text-slate-800">{stages.flatMap(getServices).length}</span>
          </span>
          <span>
            Итого:{' '}
            <span className="text-base font-semibold text-slate-900">
              {totalAmount.toLocaleString('ru-RU')} ₽
            </span>
          </span>
        </div>
      ) : null}
    </div>
  )
}
